import React from "react";
import { MessageCircle } from "lucide-react";

/**
 * Sanitize a phone into E.164 digits-only for wa.me.
 *
 * wa.me requires country code without the leading `+`. If the number has no
 * country code (typical for Brazilian users who just type "11 99999-9999"),
 * we prepend `55` (Brazil) — the dealership's default locale.
 */
export function sanitizePhoneForWhatsApp(phone, defaultCountry = "55") {
  const digits = String(phone || "").replace(/\D/g, "");
  if (!digits) return "";
  // Already has a country code (>= 11 digits usually means DDI+DDD+num).
  // Brazilian mobiles are 11 digits (DDD + 9-digit number). If we see 10 or
  // 11 digits, assume it's a local number and prepend the default country.
  if (digits.length <= 11) return `${defaultCountry}${digits}`;
  return digits;
}

/**
 * WhatsAppButton — opens WhatsApp Web/App with a pre-composed message.
 *
 * Uses the `wa.me` deep-link protocol → no API keys, no server involvement.
 * The user still confirms + sends the message manually inside WhatsApp, which
 * is perfect for a "one-click contact" flow.
 *
 * Props:
 *   phone       (string, required) — buyer/lead/employee phone. Any format.
 *   message     (string, optional) — pre-fill text. URL-encoded internally.
 *   label       (string, optional) — text next to the icon. Omit for icon-only.
 *   testid      (string, optional) — data-testid for automated tests.
 *   size        (number, optional) — icon size (default 14).
 *   className   (string, optional) — extra classes appended to the default style.
 *   variant     ("chip"|"button"|"icon") — visual style. Default "chip".
 */
export default function WhatsAppButton({
  phone,
  message = "",
  label,
  testid = "whatsapp-btn",
  size = 14,
  className = "",
  variant = "chip",
  onClick,
}) {
  const digits = sanitizePhoneForWhatsApp(phone);
  const disabled = !digits;

  const href = disabled
    ? undefined
    : `https://wa.me/${digits}${message ? `?text=${encodeURIComponent(message)}` : ""}`;

  const base =
    variant === "icon"
      ? "inline-flex items-center justify-center w-7 h-7 text-success hover:bg-success/15 transition-colors"
      : variant === "button"
      ? "inline-flex items-center gap-2 px-3 h-9 bg-success text-white hover:brightness-110 transition-colors text-xs font-display font-bold uppercase tracking-widest"
      : "inline-flex items-center gap-1.5 px-2.5 h-7 border border-success/50 text-success hover:bg-success/10 transition-colors text-[10px] font-display font-bold uppercase tracking-widest";

  const disabledCls = disabled ? "opacity-40 cursor-not-allowed pointer-events-none" : "";

  return (
    <a
      href={href}
      target={disabled ? undefined : "_blank"}
      rel="noopener noreferrer"
      data-testid={testid}
      onClick={onClick}
      title={disabled ? "Sem telefone cadastrado" : "Abrir WhatsApp"}
      aria-disabled={disabled}
      className={`${base} ${disabledCls} ${className}`}
    >
      <MessageCircle size={size} />
      {label && variant !== "icon" && <span>{label}</span>}
    </a>
  );
}
